import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Loader2 } from "lucide-react";

import { AppDialog } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/features/auth";
import { useUserStore } from "../hooks";
import { Button } from "@/components/ui/button";

const ADMIN_ROLES = ["system_admin", "business_unit"];

const formatRoleLabel = (role = "") =>
  role
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const UserViewDialog = observer(({ user, onClose }) => {
  const { t } = useTran();
  const { userRole } = useAuthStore();
  const {
    loadManagedUser,
    getEditForm,
    getEditMeta,
    isLoading,
    getError,
    getUser,
    invalidateUser,
  } = useUserStore();
  const [reloadKey, setReloadKey] = useState(0);

  const viewDialogOpen = Boolean(user);
  const viewDialogUser = user;

  useEffect(() => {
    if (!viewDialogOpen || !viewDialogUser?.id) return;

    let isActive = true;
    const fetchDetails = async () => {
      try {
        await loadManagedUser(viewDialogUser.id, { force: reloadKey > 0 });
      } catch (_error) {
        if (!isActive) return;
      }
    };

    fetchDetails();

    return () => {
      isActive = false;
    };
  }, [viewDialogOpen, viewDialogUser?.id, reloadKey, loadManagedUser]);

  const editForm = getEditForm(viewDialogUser?.id);
  const meta = getEditMeta(viewDialogUser?.id);
  const loading = Boolean(viewDialogUser?.id && isLoading(viewDialogUser.id));
  const loadError = viewDialogUser?.id ? getError(viewDialogUser.id) : "";
  const storedUser = viewDialogUser?.id ? getUser(viewDialogUser.id) : null;
  const resolvedUser = storedUser
    ? { ...(viewDialogUser || {}), ...storedUser }
    : viewDialogUser || {};

  const isCompanyAdmin = ADMIN_ROLES.includes(userRole);
  const displayName =
    editForm.name ||
    resolvedUser.displayName ||
    resolvedUser.name ||
    t("Not available");
  const username =
    editForm.username || resolvedUser.username || t("Not available");
  const email = editForm.email || resolvedUser.email || t("Not available");
  const organizationLabel =
    editForm.organizationUnitName ||
    resolvedUser.organizationUnitName ||
    editForm.organizationUnitId ||
    resolvedUser.organizationUnitId ||
    t("Not assigned");
  const companyLabel =
    meta.company || resolvedUser.company || t("Not assigned");
  const roleLabel = resolvedUser.role
    ? formatRoleLabel(resolvedUser.role)
    : t("Not available");
  const isNewUser = resolvedUser?.isNew === true;
  const statusLabel = isNewUser
    ? t("Awaiting Login")
    : resolvedUser.status
      ? formatRoleLabel(resolvedUser.status)
      : t("Not available");

  const permissionOverrides = resolvedUser?.permissionOverrides || {};
  const overrideMode =
    permissionOverrides.mode === "override" ? "override" : "inherit";
  const overrideAllow = Array.isArray(permissionOverrides.allow)
    ? permissionOverrides.allow
    : [];
  const overrideDeny = Array.isArray(permissionOverrides.deny)
    ? permissionOverrides.deny
    : [];
  const effectivePermissions = Array.isArray(resolvedUser?.effectivePermissions)
    ? resolvedUser.effectivePermissions
    : [];

  const showEmptyState = !viewDialogUser?.id;

  const handleRetry = () => {
    if (!viewDialogUser?.id) return;
    invalidateUser(viewDialogUser.id);
    setReloadKey((prev) => prev + 1);
  };

  return (
    <AppDialog
      open={viewDialogOpen}
      onOpenChange={(nextOpen) => !nextOpen && onClose?.()}
      title={t("User Information")}
      size="lg"
    >
      {showEmptyState ? (
        <p className="text-sm text-muted-foreground">
          {t("Select a user to view details.")}
        </p>
      ) : loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("Loading user details...")}
        </div>
      ) : loadError ? (
        <div className="space-y-3 rounded-radius border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          <p>{loadError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRetry}
          >
            {t("Try again")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-radius border border-border bg-muted/20 p-4">
            <p className="text-lg font-semibold text-foreground">
              {displayName}
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("Username")}
                </p>
                <p className="text-sm text-foreground">{username}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("Email")}
                </p>
                <p className="text-sm text-foreground">{email}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("Role")}
                </p>
                <p className="text-sm text-foreground">{roleLabel}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {isCompanyAdmin ? t("Company") : t("Organization")}
                </p>
                <p className="text-sm text-foreground">
                  {isCompanyAdmin ? companyLabel : organizationLabel}
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-radius border border-border bg-background/60 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("Status")}
              </p>
              <Badge variant="outline">{statusLabel}</Badge>
            </div>
            <div className="rounded-radius border border-border bg-background/60 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("Created at")}
              </p>
              <p className="text-sm text-foreground">
                {meta.createdAt
                  ? new Date(meta.createdAt).toLocaleString()
                  : t("Not available")}
              </p>
            </div>
          </div>

          <div className="space-y-2 rounded-radius border border-border bg-muted/20 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground">
                {t("User Overrides")}
              </h3>
              <span className="text-xs text-muted-foreground">
                {t("Mode")}:{" "}
                {overrideMode === "override" ? t("Override") : t("Inherit")}
              </span>
            </div>
            {overrideAllow.length > 0 || overrideDeny.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("Allow")}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {overrideAllow.length > 0
                      ? overrideAllow.map((label) => (
                          <span
                            key={`allow-${label}`}
                            className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
                          >
                            {label}
                          </span>
                        ))
                      : t("No allow overrides")}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("Deny")}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {overrideDeny.length > 0
                      ? overrideDeny.map((label) => (
                          <span
                            key={`deny-${label}`}
                            className="rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700"
                          >
                            {label}
                          </span>
                        ))
                      : t("No deny overrides")}
                  </div>
                </div>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">
                {t("No user-specific overrides")}
              </span>
            )}
          </div>

          <div className="space-y-2 rounded-radius border border-border bg-muted/20 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground">
                {t("Effective Permissions")}
              </h3>
              <span className="text-xs text-muted-foreground">
                {t("Total")}: {effectivePermissions.length}
              </span>
            </div>
            {effectivePermissions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {effectivePermissions.map((permission) => (
                  <span
                    key={permission}
                    className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground"
                  >
                    {permission}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">
                {t("No effective permissions")}
              </span>
            )}
          </div>
        </div>
      )}
    </AppDialog>
  );
});

export default UserViewDialog;
